import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import { ShieldCheck, AlertTriangle, Search, Download, RefreshCw, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_STYLE = {
  team: 'bg-[#002FA7]/10 text-[#002FA7]',
  driver: 'bg-emerald-50 text-emerald-700',
  payroll: 'bg-amber-50 text-amber-700',
  load: 'bg-slate-100 text-slate-700',
};

const ACTION_LABEL = {
  'user.create': 'Created user',
  'user.update': 'Updated user',
  'user.delete': 'Deleted user',
  'invite.sent': 'Sent invite',
  'invite.resent': 'Resent invite',
  'invite.revoked': 'Revoked invite',
  'invite.accepted': 'Accepted invite',
  'driver.approved': 'Approved driver',
  'driver.deactivated': 'Deactivated driver',
  'payroll.finalized': 'Finalized payroll',
  'payroll.recalculated': 'Recalculated payroll',
  'load.status_override': 'Reopened delivered load',
};

const AuditCompliance = () => {
  const [data, setData] = useState({ events: [], suspicious: [], totals_24h: {} });
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('all');
  const [actor, setActor] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async ({ cat, act } = {}) => {
    setRefreshing(true);
    try {
      const params = new URLSearchParams({ limit: '200' });
      const c = cat ?? category;
      const a = act ?? actor;
      if (c && c !== 'all') params.set('category', c);
      if (a) params.set('actor', a);
      const { data } = await axios.get(`${API}/admin/compliance/overview?${params}`, { withCredentials: true });
      setData(data);
    } catch (err) {
      toast.error(err.response?.status === 403 ? 'Super admin only' : 'Could not load compliance data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [category, actor]);

  useEffect(() => { fetchData(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const onCategoryChange = (c) => { setCategory(c); fetchData({ cat: c }); };
  const onActorSubmit = (e) => { e.preventDefault(); fetchData({ act: actor }); };

  const downloadCsv = async () => {
    try {
      const params = new URLSearchParams({ days: '90' });
      if (category && category !== 'all') params.set('category', category);
      const res = await axios.get(`${API}/user-audit-logs/export.csv?${params}`, {
        withCredentials: true, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `compliance_${category}_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch {
      toast.error('Could not export CSV');
    }
  };

  const totals = useMemo(() => ({
    team: data.totals_24h?.team || 0,
    driver: data.totals_24h?.driver || 0,
    payroll: data.totals_24h?.payroll || 0,
    load: data.totals_24h?.load || 0,
  }), [data.totals_24h]);

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8" data-testid="audit-compliance-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
          <div>
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-6 h-6 text-[#002FA7]" />
              <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                Compliance
              </h1>
            </div>
            <p className="text-sm text-slate-600 mt-1">Cross-tenant audit stream · super admin only.</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="compliance-refresh"
              onClick={() => fetchData()}
              className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700 border border-slate-300 rounded-md px-3 py-2 hover:bg-slate-50"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
            </button>
            <button
              data-testid="compliance-csv"
              onClick={downloadCsv}
              className="inline-flex items-center gap-2 bg-[#002FA7] text-white text-sm font-semibold rounded-md px-3 py-2 hover:bg-[#00247D]"
            >
              <Download className="w-4 h-4" /> Export CSV
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            ['team', 'Team'],
            ['driver', 'Driver'],
            ['payroll', 'Payroll'],
            ['load', 'Load'],
          ].map(([k, label]) => (
            <div key={k} data-testid={`totals-${k}`} className="bg-white border border-slate-200 rounded-md p-4">
              <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">{label}</p>
              <p className="text-2xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                {totals[k]}
              </p>
              <p className="text-xs text-slate-500 mt-0.5">last 24h</p>
            </div>
          ))}
        </div>

        {data.suspicious.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6" data-testid="suspicious-panel">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-4 h-4 text-red-600" />
              <h2 className="text-sm font-bold text-red-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                Suspicious activity (last 24h)
              </h2>
            </div>
            <ul className="space-y-1.5">
              {data.suspicious.map((s, i) => (
                <li key={i} className="text-sm text-red-900 flex flex-wrap gap-2" data-testid={`suspicious-row-${i}`}>
                  <span className="font-semibold">{s.actor_email || 'unknown actor'}</span>
                  <span className="text-red-700">·</span>
                  <span>{ACTION_LABEL[s.action] || s.action}</span>
                  <span className="text-red-700">×{s.count_24h}</span>
                  {s.tenants_touched > 1 && <span className="text-red-700">across {s.tenants_touched} tenants</span>}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200 flex flex-col sm:flex-row gap-3">
            <form onSubmit={onActorSubmit} className="relative flex-1">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
              <input
                data-testid="compliance-actor-search"
                value={actor} onChange={(e) => setActor(e.target.value)}
                placeholder="Search actor email…"
                className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
              />
            </form>
            <div className="flex gap-1 flex-wrap">
              {['all', 'team', 'driver', 'payroll', 'load'].map((c) => (
                <button
                  key={c}
                  data-testid={`compliance-filter-${c}`}
                  onClick={() => onCategoryChange(c)}
                  className={`text-xs font-semibold px-2.5 py-1.5 rounded ${
                    category === c ? 'bg-[#002FA7] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {c === 'all' ? 'All' : c.charAt(0).toUpperCase() + c.slice(1)}
                </button>
              ))}
            </div>
          </div>
          {loading ? (
            <div className="p-10 text-center text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
          ) : data.events.length === 0 ? (
            <div className="p-10 text-center text-slate-500" data-testid="compliance-empty">
              No audit rows match the current filter.
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-widest text-slate-500">Time</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-widest text-slate-500">Category</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-widest text-slate-500">Action</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-widest text-slate-500">Actor</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-widest text-slate-500">Target</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-widest text-slate-500">Tenant</th>
                </tr>
              </thead>
              <tbody>
                {data.events.map((e) => (
                  <tr key={e.id} data-testid={`compliance-row-${e.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">
                      {new Date(e.at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${CATEGORY_STYLE[e.category] || 'bg-slate-100'}`}>
                        {e.category || 'team'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-slate-900">{ACTION_LABEL[e.action] || e.action}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{e.actor_email || 'system'}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{e.target_email || e.meta?.load_ref || e.meta?.driver_name || '—'}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{e.company_name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AuditCompliance;
